import {
    computed,
    toRef,
    type Ref,
    type WritableComputedRef,
    type ComputedRef,
    ref,
    watch,
} from 'vue'
import { useRouter } from 'vue-router'

import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import {
    type CatalogueEngagementRingsFilterInput,
    type CatalogueSortOrder,
    type EngagementRingDesignFeatureLabelCode,
    type EngagementRingDesignStyleLabelCode,
    type EngagementRingFiltersFragment,
    type EngagementRingStyleLabelCode,
    type MetalCode,
    type StoneShapeCode,
} from '@/graphql/api'
import { useCountryPlugin } from '@/plugins/country.ts'
import { type EngagementRingsRoute, useRoutes } from '@/router/routes'
import type { Props } from '../EngagementRings.vue'
import { useFiltersCount } from '../../useFiltersCount'
import { useSearch } from '../../composables/useSearch'
import { useFormattedPriceRange } from '@/composables/useMoneyFormatter'
import type { FilterChip } from '@/components/ui/filter/FilterChips.vue'
import { systemCodeToWords } from '@/helpers'
import type { InputRangeModel } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'

export function useEngagementRingsFilters(
    props: Props,
    filters: Readonly<Ref<EngagementRingFiltersFragment | undefined>>
): {
    filterRoutes: {
        routeReset: EngagementRingsRoute
        getRoutePage: (page: number) => EngagementRingsRoute
        getRoutePrice: (values: InputRangeModel) => ButtonRouteRelative
        getRouteAddMetal: (metal: MetalCode) => EngagementRingsRoute
        getRouteRemoveMetal: () => EngagementRingsRoute
        getRouteAddRingStyle: (
            ringStyle: EngagementRingStyleLabelCode
        ) => EngagementRingsRoute
        getRouteRemoveRingStyle: () => EngagementRingsRoute
        getRouteAddCentreStoneShape: (
            shape: StoneShapeCode
        ) => EngagementRingsRoute
        getRouteRemoveCentreStoneShape: () => EngagementRingsRoute
        getRouteAddDesignStyle: (
            designStyle: EngagementRingDesignStyleLabelCode
        ) => EngagementRingsRoute
        getRouteRemoveDesignStyle: () => EngagementRingsRoute
        getRouteAddDesignFeature: (
            designFeature: EngagementRingDesignFeatureLabelCode
        ) => EngagementRingsRoute
        getRouteRemoveDesignFeature: () => EngagementRingsRoute
        getRouteSort: (sortBy: CatalogueSortOrder) => EngagementRingsRoute
    }
    filterInput: WritableComputedRef<CatalogueEngagementRingsFilterInput>
    filterInputWrapper: WritableComputedRef<CatalogueEngagementRingsFilterInput>
    fetchingProxied: Ref<boolean>
    activeFilterCount: ComputedRef<number>
    filterChips: ComputedRef<FilterChip[]>
    search: Ref<string>
} {
    const routes = useRoutes()
    const { countryCode } = useCountryPlugin()
    const currency = useCurrencyInput()
    const router = useRouter()

    const getRoute = (
        filter: CatalogueEngagementRingsFilterInput,
        page?: number,
        sortBy?: CatalogueSortOrder
    ): EngagementRingsRoute => {
        const metal = filter.primaryMetalCodes?.[0]
        const style = filter.ringStyles?.[0]
        const shape = filter.centreStoneShapes?.[0]
        const designStyle = filter.designStyles?.[0]
        const designFeatures = filter.designFeatures ?? undefined
        const priceMin = filter.price?.min
        const priceMax = filter.price?.max

        return routes.engagementRings({
            metal,
            ringStyle: style,
            shape: shape,
            designStyle: designStyle,
            designFeatures: designFeatures,
            priceMax: priceMax ? Number(priceMax) : undefined,
            priceMin: priceMin ? Number(priceMin) : undefined,
            page,
            sortBy: sortBy ?? props.sortBy,
        })
    }

    const filterInput = computed<CatalogueEngagementRingsFilterInput>({
        get() {
            const designFeatures = props.designFeatures
                ? Array.isArray(props.designFeatures)
                    ? props.designFeatures
                    : [props.designFeatures]
                : []

            return {
                centreStoneShapes: props.shape ? [props.shape] : [],
                ringStyles: props.ringStyle ? [props.ringStyle] : [],
                designStyles: props.designStyle ? [props.designStyle] : [],
                designFeatures,
                primaryMetalCodes: props.metal ? [props.metal] : [],
                price: {
                    country: countryCode.value!,
                    currency: currency.value,
                    min: props.priceMin ? String(props.priceMin) : null,
                    max: props.priceMax ? String(props.priceMax) : null,
                },
                search: search.value !== '' ? search.value : null,
            }
        },
        set(routeVariables: CatalogueEngagementRingsFilterInput) {
            router.push(getRoute(routeVariables, 1))
        },
    })

    const filterRoutes = {
        routeReset: routes.engagementRings({}),

        getRoutePage: (page: number) => getRoute(filterInput.value, page),

        getRoutePrice: (values: InputRangeModel): EngagementRingsRoute =>
            getRoute({
                ...filterInput.value,
                price: {
                    country: countryCode.value!,
                    currency: currency.value,
                    min: String(values[0]),
                    max: String(values[1]),
                },
            }),

        getRouteAddMetal: (metal: MetalCode) =>
            getRoute({
                ...filterInput.value,
                primaryMetalCodes: [metal],
            }),

        getRouteRemoveMetal: () =>
            getRoute({
                ...filterInput.value,
                primaryMetalCodes: [],
            }),

        getRouteAddRingStyle: (ringStyle: EngagementRingStyleLabelCode) =>
            getRoute({
                ...filterInput.value,
                ringStyles: [ringStyle],
            }),

        getRouteRemoveRingStyle: () =>
            getRoute({
                ...filterInput.value,
                ringStyles: [],
            }),

        getRouteAddCentreStoneShape: (shape: StoneShapeCode) =>
            getRoute({
                ...filterInput.value,
                centreStoneShapes: [shape],
            }),

        getRouteRemoveCentreStoneShape: () =>
            getRoute({
                ...filterInput.value,
                centreStoneShapes: [],
            }),

        getRouteAddDesignStyle: (
            designStyle: EngagementRingDesignStyleLabelCode
        ) =>
            getRoute({
                ...filterInput.value,
                designStyles: [designStyle],
            }),

        getRouteRemoveDesignStyle: () =>
            getRoute({
                ...filterInput.value,
                designStyles: [],
            }),

        getRouteAddDesignFeature: (
            designFeature: EngagementRingDesignFeatureLabelCode
        ) =>
            getRoute({
                ...filterInput.value,
                designFeatures: [designFeature],
            }),

        getRouteRemoveDesignFeature: () =>
            getRoute({
                ...filterInput.value,
                designFeatures: [],
            }),

        getRouteSort: (sortBy: CatalogueSortOrder) =>
            getRoute(filterInput.value, 1, sortBy),
    }

    const { search } = useSearch(
        toRef(() => props.page),
        filterRoutes.getRoutePage
    )

    const activeFilterCount = useFiltersCount(
        computed(() => [
            !!filterInput.value?.primaryMetalCodes?.length,
            !!filterInput.value?.ringStyles?.length,
            !!filterInput.value?.centreStoneShapes?.length,
            !!filterInput.value?.designStyles?.length,
            !!filterInput.value?.designFeatures?.length,
            !!(filterInput.value?.price?.max || filterInput.value?.price?.min),
        ])
    )

    const formattedPriceRange = useFormattedPriceRange(
        computed(() => ({
            min: { amount: props.priceMin },
            max: { amount: props.priceMax },
        }))
    )

    const filterChips = computed(() => {
        const chips: FilterChip[] = []

        const primaryMetal = filterInput.value.primaryMetalCodes?.[0]
        if (primaryMetal) {
            chips.push({
                label:
                    filters.value?.primaryMetals.find(
                        (metal) => metal.metal.systemCode === primaryMetal
                    )?.metal.translation.name ||
                    systemCodeToWords(primaryMetal),
                getRouteRemove: filterRoutes.getRouteRemoveMetal,
            })
        }

        const ringStyle = filterInput.value.ringStyles?.[0]
        if (ringStyle) {
            chips.push({
                label:
                    filters.value?.ringStyles.find(
                        (style) => style.label.systemCode === ringStyle
                    )?.label.translation.name || systemCodeToWords(ringStyle),
                getRouteRemove: filterRoutes.getRouteRemoveRingStyle,
            })
        }

        const centreStoneShape = filterInput.value.centreStoneShapes?.[0]
        if (centreStoneShape) {
            chips.push({
                label:
                    filters.value?.centreStoneShapes.find(
                        (shape) => shape.shape.systemCode === centreStoneShape
                    )?.shape.translation.name ||
                    systemCodeToWords(centreStoneShape),
                getRouteRemove: filterRoutes.getRouteRemoveCentreStoneShape,
            })
        }

        const designStyle = filterInput.value.designStyles?.[0]
        if (designStyle) {
            chips.push({
                label:
                    filters.value?.designStyles.find(
                        (style) => style.label.systemCode === designStyle
                    )?.label.translation.name || systemCodeToWords(designStyle),
                getRouteRemove: filterRoutes.getRouteRemoveDesignStyle,
            })
        }

        if (filterInput.value.designFeatures?.length) {
            filterInput.value.designFeatures.forEach((feature) => {
                chips.push({
                    label:
                        filters.value?.designFeatures.find(
                            (f) => f.label.systemCode === feature
                        )?.label.translation.name || systemCodeToWords(feature),
                    getRouteRemove: filterRoutes.getRouteRemoveDesignFeature,
                })
            })
        }

        if (formattedPriceRange.value) {
            chips.push({
                label: formattedPriceRange.value,
                getRouteRemove: () =>
                    getRoute({
                        ...filterInput.value,
                        price: undefined,
                    }),
            })
        }

        return chips
    })

    // Make sure loading indicator render runs before
    // product data renders - optimization for INP metric
    // Cached product data takes longer to render than just indicator render
    const filterInputProxy = ref(filterInput.value)

    if (!import.meta.env.SSR) {
        let count = 0

        watch(filterInput, (v) => {
            fetchingProxied.value = true

            // breaks suspense if first timeout delays is 0
            count += 1
            // eslint-disable-next-line no-restricted-globals
            window.setTimeout(
                () => {
                    filterInputProxy.value = v
                },
                count > 1 ? 0 : 1000
            )
        })
    }

    const fetchingProxied = ref(false)
    const filterInputWrapper = computed({
        get() {
            return filterInputProxy.value
        },
        set(routeVariables: CatalogueEngagementRingsFilterInput) {
            filterInput.value = routeVariables
        },
    })

    return {
        filterRoutes,
        filterInput,
        activeFilterCount,
        filterChips,
        search,
        filterInputWrapper,
        fetchingProxied,
    }
}
